import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { ReasonLogo, ReasonLogoFull, DpiitChip, ReasonFooter, REASON_COMPANY } from "@/components/brand/ReasonLogo";
import { TID } from "@/constants/testIds";
import { formatApiError, api } from "@/lib/api";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { applyBrandPrimary, clearBrandPrimary, LOGIN_BRAND_KEY } from "@/lib/brandColor";

export default function Login() {
  const { login, verifyMfaLogin, resetSession } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaStep, setMfaStep] = useState(null); // { challengeToken }
  const [mfaCode, setMfaCode] = useState("");
  // E1C R2 — single active session conflict (block-with-choice)
  const [sessionConflict, setSessionConflict] = useState(null); // { resetToken }

  // E1C R2 — show a one-time "session expired" notice when a protected call 401'd us back to login
  // (logout elsewhere / session taken over on another device / admin terminate / disabled).
  useEffect(() => {
    try {
      if (sessionStorage.getItem("reason_session_expired")) {
        sessionStorage.removeItem("reason_session_expired");
        toast.info("Your session has expired. Please sign in again.");
      }
    } catch { /* ignore */ }
  }, []);

  // "Brand Everywhere" — re-brand the login for the last workspace used on this device.
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOGIN_BRAND_KEY);
      if (raw) {
        const b = JSON.parse(raw);
        if (b && (b.company_name || b.logo_data_uri)) {
          setBrand(b);
          if (b.primary_color) applyBrandPrimary(b.primary_color);
        }
      }
    } catch { /* ignore */ }
  }, []);

  const forgetBrand = () => {
    try { localStorage.removeItem(LOGIN_BRAND_KEY); } catch { /* noop */ }
    clearBrandPrimary();
    setBrand(null);
  };

  const goNext = async (u) => {
    toast.success(`Welcome back, ${u.name || u.email}`);
    // Issue 2 — never reuse the PREVIOUS user's route (loc.state.from). Always send each user to
    // their own role-based default landing, which they are guaranteed to be able to access.
    let dest = "/app";
    try { dest = (await api.get("/me/home")).data?.route || "/app"; } catch { /* fallback */ }
    nav(dest, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res.sessionConflict) {
        setSessionConflict({ resetToken: res.resetToken });
      } else if (res.mfaRequired) {
        setMfaStep({ challengeToken: res.challengeToken });
        toast.info("Enter the code from your authenticator app");
      } else {
        goNext(res.user);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await verifyMfaLogin(mfaStep.challengeToken, mfaCode.trim());
      if (res.sessionConflict) {
        setMfaStep(null);
        setSessionConflict({ resetToken: res.resetToken });
      } else {
        goNext(res.user);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async () => {
    setBusy(true);
    try {
      const res = await resetSession(sessionConflict.resetToken);
      setSessionConflict(null);
      goNext(res.user);
    } catch (err) {
      toast.error(formatApiError(err));
      setSessionConflict(null); // challenge is single-use/expired — send them back to sign in again
    } finally {
      setBusy(false);
    }
  };

  const cancelConflict = () => {
    // Cancel leaves the existing session completely unchanged.
    setSessionConflict(null);
    setPassword("");
  };

  if (sessionConflict) {
    return (
      <AuthLayout>
        <div data-testid="session-conflict-panel">
          <h1 className="font-display text-2xl font-semibold tracking-tight">You're already signed in</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This account is active on another device or browser. For your security only one active session
            is allowed at a time. You can reset that session and sign in here, or cancel.
          </p>
          <div className="mt-7 space-y-3">
            <Button data-testid="session-reset-confirm" onClick={doReset} disabled={busy}
                    className="w-full bg-primary hover:bg-primary/90">
              {busy ? "Signing in…" : "Reset current session & sign in here"}
            </Button>
            <Button data-testid="session-reset-cancel" type="button" variant="ghost" className="w-full"
                    onClick={cancelConflict} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (mfaStep) {
    return (
      <AuthLayout>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Two-factor verification</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app, or a backup code.</p>
        <form onSubmit={submitMfa} className="mt-7 space-y-4">
          <div>
            <Label className="text-xs">Verification code</Label>
            <Input
              data-testid="mfa-login-code"
              value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
              placeholder="123456 or backup code" autoFocus required
            />
          </div>
          <Button data-testid="mfa-login-submit" type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={busy}>
            {busy ? "Verifying…" : "Verify & sign in"}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={() => { setMfaStep(null); setMfaCode(""); }}>
            Back
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {brand ? (
        <div className="mb-6" data-testid="login-tenant-brand">
          {brand.show_logo !== false && brand.logo_data_uri ? (
            <img src={brand.logo_data_uri} alt={brand.company_name}
              data-testid="login-tenant-logo"
              className="h-11 w-auto max-w-[180px] object-contain mb-4" />
          ) : (
            <div className="h-11 w-11 rounded-lg grid place-items-center text-white mb-4"
              style={{ backgroundColor: brand.primary_color || "#0F766E" }}>
              <span className="font-black text-lg">{(brand.company_name || "?").slice(0, 1).toUpperCase()}</span>
            </div>
          )}
          <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="login-tenant-name">
            Sign in to {brand.company_name || "your workspace"}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Use your work email and password.</p>
        </div>
      ) : (
        <>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Sign in to REASON RetailOS™</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Use your work email and password.</p>
        </>
      )}

      <form onSubmit={submit} className="mt-7 space-y-4">
        <div>
          <Label className="text-xs">Email</Label>
          <Input
            data-testid={TID.loginEmail}
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
          />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Label className="text-xs">Password</Label>
            <Link to="#" className="text-xs text-muted-foreground hover:text-foreground">Forgot?</Link>
          </div>
          <Input
            data-testid={TID.loginPassword}
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
          />
        </div>
        <Button data-testid={TID.loginSubmit} type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      {brand && (
        <button type="button" onClick={forgetBrand} data-testid="login-forget-brand"
          className="mt-2 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
          Not {brand.company_name || "this workspace"}? Sign in to a different workspace
        </button>
      )}
    </AuthLayout>
  );
}

export function AuthLayout({ children }) {
  const features = [
    "Unified commerce — POS, e-com, marketplace & store ops in one OS",
    "Real-time inventory, merchandising, pricing & promotions",
    "AI decisioning, analytics & enterprise governance built-in",
  ];
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-background">
      {/* Brand panel */}
      <div
        className="relative hidden lg:flex flex-col justify-between overflow-hidden p-12 text-white"
        style={{ background: `linear-gradient(150deg, #16356B 0%, #1C3F7E 45%, #11254A 100%)` }}
      >
        {/* analytics motif */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.16]"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)", backgroundSize: "46px 46px" }} />
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full blur-3xl" style={{ background: "radial-gradient(circle,#22CAD144,transparent 70%)" }} />
        <div className="pointer-events-none absolute bottom-0 left-0 h-80 w-80 rounded-full blur-3xl" style={{ background: "radial-gradient(circle,#F47C2433,transparent 70%)" }} />
        <div className="pointer-events-none absolute bottom-12 right-12 flex items-end gap-2 opacity-30">
          {[40, 64, 52, 88, 72, 110].map((h, i) => (
            <span key={i} className="w-6 rounded-t-sm" style={{ height: h, background: i % 3 === 0 ? "#F47C24" : i % 3 === 1 ? "#22CAD1" : "#ffffff" }} />
          ))}
        </div>

        <div className="relative">
          <Link to="/"><ReasonLogoFull width={150} /></Link>
        </div>

        <div className="relative max-w-lg">
          <h2 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.08] tracking-tight">
            The operating system for <span style={{ color: "#F8B07F" }}>global retail</span>.
          </h2>
          <p className="mt-5 text-base text-white/70 leading-relaxed">
            From the till to the executive command center — one enterprise platform that unifies every retail operation, powered by data and AI.
          </p>
          <ul className="mt-8 space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-white/90">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full" style={{ background: "#22CAD1" }}>
                  <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="#0B2447" strokeWidth="3.5"><path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex items-center gap-3 text-xs text-white/60">
          <DpiitChip />
          <span className="text-white/70 font-medium">{REASON_COMPANY.legalName}</span>
        </div>
      </div>

      {/* Form panel */}
      <div className="relative flex flex-col px-6 sm:px-10 py-8">
        <div className="absolute top-5 right-5 z-10"><LanguageSwitcher variant="outline" /></div>
        <div className="lg:hidden pt-2"><Link to="/"><ReasonLogo /></Link></div>
        <div className="flex-1 grid place-items-center">
          <div className="w-full max-w-sm">{children}</div>
        </div>
        <div className="text-xs text-muted-foreground">
          <ReasonFooter compact />
        </div>
      </div>
    </div>
  );
}
